import { Product, ProductVariant } from '../types/store';

import vanillaImg from '../assets/images/malrich_vanilla_exact_1786827973901.webp';
import mangoImg from '../assets/images/malrich_mango_exact_1786827989910.webp';
import chocolateImg from '../assets/images/malrich_chocolate_exact_1786828005205.webp';
import strawberryImg from '../assets/images/malrich_strawberry_exact_1786828023520.webp';
import bundleImg from '../assets/images/malrich_bundle_1786823386878.webp';
import heroBannerImg from '../assets/images/malrich_hero_main_banner_1786910615726.webp';

export const ASSET_IMAGES = {
  vanilla: vanillaImg,
  mango: mangoImg,
  chocolate: chocolateImg,
  strawberry: strawberryImg,
  bundle: bundleImg,
  heroBanner: heroBannerImg,
  fullBanner: heroBannerImg,
};

export const CANONICAL_STATIC_IMAGES = {
  vanilla: vanillaImg,
  mango: mangoImg,
  chocolate: chocolateImg,
  strawberry: strawberryImg,
  bundle: bundleImg,
  heroBanner: heroBannerImg,
};

export const CANONICAL_JPG_FALLBACKS = {
  vanilla: vanillaImg,
  mango: mangoImg,
  chocolate: chocolateImg,
  strawberry: strawberryImg,
  bundle: bundleImg,
};

/**
 * Returns a guaranteed canonical fallback image based on flavor, id or title
 */
export function getCanonicalFallbackImage(flavor?: string, id?: string, title?: string): string {
  const target = `${flavor || ''} ${id || ''} ${title || ''}`.toLowerCase();
  if (target.includes('bundle') || target.includes('deal') || target.includes('buy 2')) {
    return bundleImg;
  }
  if (target.includes('mango')) {
    return mangoImg;
  }
  if (target.includes('choc')) {
    return chocolateImg;
  }
  if (target.includes('straw')) {
    return strawberryImg;
  }
  return vanillaImg;
}

/**
 * Normalizes any image URL (Firebase Storage, CDN, S3, Base64, Vite asset, or legacy path)
 * into a valid browser-loadable image URL without ever dropping or reverting valid custom user images.
 */
export function normalizeProductImageUrl(
  url?: string | null,
  fallbackFlavor?: string,
  fallbackId?: string,
  fallbackTitle?: string
): string {
  if (!url || typeof url !== 'string') {
    return getCanonicalFallbackImage(fallbackFlavor, fallbackId, fallbackTitle);
  }
  const clean = url.trim();
  if (clean.length < 3 || clean === 'undefined' || clean === 'null' || clean.includes('[object')) {
    return getCanonicalFallbackImage(fallbackFlavor, fallbackId, fallbackTitle);
  }

  // 1. Firebase Storage, Google Cloud Storage, Cloudinary, AWS S3, or any live HTTPS/HTTP link
  if (clean.startsWith('https://') || clean.startsWith('http://') || clean.startsWith('//')) {
    return clean;
  }

  // 2. Base64 uploaded data URI
  if (clean.startsWith('data:image/')) {
    return clean;
  }

  // 3. Match against known flavor asset files
  const lower = clean.toLowerCase();
  if (lower.includes('vanilla')) return vanillaImg;
  if (lower.includes('mango')) return mangoImg;
  if (lower.includes('choc')) return chocolateImg;
  if (lower.includes('straw')) return strawberryImg;
  if (lower.includes('bundle')) return bundleImg;
  if (lower.includes('hero_main_banner') || lower.includes('hero_banner')) return heroBannerImg;

  // 4. Root relative paths
  if (clean.startsWith('/')) {
    return clean;
  }

  // 5. If it's a valid string with reasonable length, preserve it as-is
  if (clean.length > 5 && !clean.includes('undefined') && !clean.includes('null')) {
    return clean;
  }

  return getCanonicalFallbackImage(fallbackFlavor, fallbackId, fallbackTitle);
}

/**
 * Validates if an image string is non-empty and not corrupted
 */
export function isValidProductionImageUrl(url?: string | null): boolean {
  if (!url || typeof url !== 'string') return false;
  const clean = url.trim();
  if (clean.length < 4) return false;
  if (clean === 'undefined' || clean === 'null' || clean.includes('[object')) return false;
  if (clean.startsWith('blob:')) return false;
  return true;
}

/**
 * Robust image resolver that guarantees product images never disappear or show broken links.
 * Prioritizes the product's actual images (Firebase Storage, HTTPS, Data URLs, Asset imports)
 * and only falls back to bundled catalog assets if no valid image is present.
 */
export function getProductMainImage(
  product?: Partial<Product> | null,
  preferredFlavor?: string
): string {
  if (!product) return CANONICAL_STATIC_IMAGES.vanilla;

  // 1. Check the product's primary images array first (user uploaded images always win!)
  if (Array.isArray(product.images) && product.images.length > 0) {
    for (const img of product.images) {
      if (isValidProductionImageUrl(img)) {
        return normalizeProductImageUrl(img, product.flavor, product.id, product.title);
      }
    }
  }

  const targetFlavor = (preferredFlavor || product.flavor || '').toLowerCase().trim();
  const id = (product.id || '').toLowerCase().trim();
  const title = (product.title || '').toLowerCase().trim();

  // 2. If explicit preferred flavor requested, check if a matching variant has a valid image
  if (preferredFlavor && Array.isArray(product.variants) && product.variants.length > 0) {
    const matchedVariant = product.variants.find(
      (v) => (v.flavor || '').toLowerCase().trim() === targetFlavor
    );
    if (matchedVariant?.image && isValidProductionImageUrl(matchedVariant.image)) {
      return normalizeProductImageUrl(matchedVariant.image, targetFlavor, id, title);
    }
  }

  // 3. Check singular image fields for backwards compatibility with various database schemas
  const anyProduct = product as Record<string, any>;
  const singleFields = [anyProduct.image, anyProduct.imageUrl, anyProduct.mainImage, anyProduct.photo];
  for (const field of singleFields) {
    if (isValidProductionImageUrl(field)) {
      return normalizeProductImageUrl(field as string, targetFlavor, id, title);
    }
  }

  // 4. Match against canonical local high-res assets only as ultimate fallback when image slot is empty
  return getCanonicalFallbackImage(targetFlavor, id, title);
}

// Common variant map for MalRich series
export const MALRICH_VARIANTS: ProductVariant[] = [
  {
    flavor: 'Vanilla',
    title: 'MalRich Healthy Weight Gain Milk Powder',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    image: vanillaImg,
    inStock: true,
    sku: 'MAL-VAN-400',
  },
  {
    flavor: 'Mango',
    title: 'MalRich Mango Balanced Diet Supplement (500gm)',
    weight: '500g',
    price: 750,
    compareAtPrice: 950,
    image: mangoImg,
    inStock: true,
    sku: 'MAL-MNG-500',
  },
  {
    flavor: 'Chocolate',
    title: 'MalRich Chocolate Healthy Weight Gain Milk Powder',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    image: chocolateImg,
    inStock: true,
    sku: 'MAL-CHO-400',
  },
  {
    flavor: 'Strawberry',
    title: 'MalRich Healthy Weight Gain Milk Powder',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    image: strawberryImg,
    inStock: true,
    sku: 'MAL-STR-400',
  },
];

export const PRODUCTS: Product[] = [
  {
    id: 'malrich-vanilla',
    title: 'MalRich Healthy Weight Gain Milk Powder',
    slug: 'malrich-healthy-weight-gain-vanilla',
    brand: 'MalRich',
    flavor: 'Vanilla',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    badge: 'Sale',
    rating: 0,
    reviewCount: 0,
    category: 'Supplements',
    shortDescription: 'Complete dietary balanced supplement for whole family with vital Magnesium, Vitamins, Calcium, and Zinc in delicious Vanilla flavour.',
    description: 'MalRich Vanilla Healthy Weight Gain Milk Powder is scientifically formulated with premium whole milk proteins, essential micronutrients, vitamins, and minerals to promote healthy appetite, energy recovery, and natural mass building without artificial fillers. Ideal for teenagers, adults, athletes, and elderly requiring balanced dietary support.',
    keyNutrients: [
      'Magnesium: Supports muscle function & reduces fatigue',
      'Vitamins (A, B-Complex, C, D3, E): Immune and metabolic health',
      'Calcium: Enhances bone density and structural strength',
      'Zinc: Promotes vital cell renewal and nutrient absorption',
      'High-Grade Milk Protein: Supports lean muscle recovery'
    ],
    benefits: [
      'Promotes healthy weight gain & appetite stimulation',
      'Boosts daily stamina, physical vitality, and endurance',
      '100% Halal certified, gentle on digestive system',
      'Rich, creamy classic Vanilla taste that blends effortlessly with milk or water',
      'Safe and effective for men, women, teenagers, and whole family'
    ],
    usageInstructions: 'Add 2–3 level scoops (approx. 30g) of MalRich Vanilla powder into a glass of warm or chilled full-cream milk (200ml) or water. Stir or shake vigorously in a shaker bottle until completely dissolved. Consume 1–2 times daily, preferably after breakfast or post-workout.',
    storageInfo: 'Store tin tightly closed in a cool, dry place away from direct sunlight and moisture. Use within 4 weeks of opening.',
    images: [vanillaImg],
    variants: MALRICH_VARIANTS,
    inStock: true,
    isBestSeller: true,
  },
  {
    id: 'malrich-mango',
    title: 'MalRich Mango Balanced Diet Supplement (500gm)',
    slug: 'malrich-balanced-diet-supplement-mango',
    brand: 'MalRich',
    flavor: 'Mango',
    weight: '500g',
    price: 750,
    compareAtPrice: 950,
    badge: 'Sale',
    rating: 0,
    reviewCount: 0,
    category: 'Supplements',
    shortDescription: 'Daily balanced nutrition supplement enriched with essential vitamins, minerals, and refreshing natural Mango flavor.',
    description: 'MalRich Mango Balanced Diet Supplement offers a refreshing, nutrient-packed solution for daily wellness. Loaded with bioavailable minerals including Magnesium, Zinc, Calcium, and essential multivitamins, it fills dietary gaps and provides sustained all-day energy for active Pakistani families.',
    keyNutrients: [
      'Natural Mango extract & antioxidant complex',
      'Zinc & Calcium for bone and immune vitality',
      'Magnesium for cellular energy and reduced lethargy',
      'Vitamins A, C, and D for daily defense'
    ],
    benefits: [
      'Refreshes energy levels and combats midday sluggishness',
      'Improves digestion and metabolic wellness',
      'Luscious Pakistani mango flavor loved by all age groups',
      'Cost-effective high nutrition daily supplement'
    ],
    usageInstructions: 'Mix 2 tablespoons (approx. 25g) with 200ml of cold milk or water. Enjoy chilled for maximum refreshment.',
    storageInfo: 'Keep in an airtight container in a dry place. Do not refrigerate powder.',
    images: [mangoImg],
    variants: MALRICH_VARIANTS,
    inStock: true,
    isBestSeller: true,
  },
  {
    id: 'malrich-chocolate',
    title: 'MalRich Chocolate Healthy Weight Gain Milk Powder',
    slug: 'malrich-healthy-weight-gain-chocolate',
    brand: 'MalRich',
    flavor: 'Chocolate',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    badge: 'Sale',
    rating: 0,
    reviewCount: 0,
    category: 'Supplements',
    shortDescription: 'Decadent Dutch cocoa flavored weight gain milk powder fortified with high-grade proteins, Magnesium, and Zinc.',
    description: 'Satisfy your sweet tooth while building lean healthy weight. MalRich Chocolate provides a premium blend of rich cocoa, dairy proteins, and key micronutrients designed to stimulate appetite, rebuild tired muscles, and deliver sustained clean calories for natural weight gain.',
    keyNutrients: [
      'Pure Cocoa Polyphenols with natural antioxidants',
      'Essential Amino Acids for muscle tissue repair',
      'Magnesium & Zinc for optimal metabolic balance',
      'Vitamins B1, B2, B6, B12, and D3'
    ],
    benefits: [
      'Gourmet chocolate milkshake experience in every glass',
      'Accelerates healthy body mass without belly bloating',
      'Fast-acting post-exercise recovery drink',
      'Halal certified and quality assured'
    ],
    usageInstructions: 'Mix 2–3 scoops in 250ml of warm or chilled whole milk. Blend with a banana or peanut butter for an extra calorie boost!',
    storageInfo: 'Store in a dry location below 25°C. Keep lid tightly closed.',
    images: [chocolateImg],
    variants: MALRICH_VARIANTS,
    inStock: true,
    isBestSeller: true,
  },
  {
    id: 'malrich-strawberry',
    title: 'MalRich Healthy Weight Gain Milk Powder',
    slug: 'malrich-healthy-weight-gain-strawberry',
    brand: 'MalRich',
    flavor: 'Strawberry',
    weight: '400g',
    price: 2200,
    compareAtPrice: 2500,
    badge: 'Sale',
    rating: 0,
    reviewCount: 0,
    category: 'Supplements',
    shortDescription: 'Delicious pink strawberry milkshake powder with balanced vitamins and minerals for optimal health and growth.',
    description: 'MalRich Strawberry is formulated to deliver balanced nutrition with irresistible real strawberry flavor. Packed with Calcium for strong bones, Magnesium for muscular balance, and Zinc for digestive support, it is a favorite choice among growing children, youth, and active adults looking for healthy bulk.',
    keyNutrients: [
      'Strawberry fruit essence & bio-factors',
      'High Bioavailability Calcium & Phosphorus',
      'Magnesium and Zinc for enzymatic synthesis',
      'Multivitamin complex'
    ],
    benefits: [
      'Delightful creamy strawberry flavor',
      'Supports healthy growth, height, and weight development',
      'Helps overcome picky eating and poor appetite',
      'Easy to digest and absorb'
    ],
    usageInstructions: 'Stir 2 scoops (30g) into 200ml cold milk. Whisk thoroughly until smooth.',
    storageInfo: 'Keep sealed in a cool, dry place.',
    images: [strawberryImg],
    variants: MALRICH_VARIANTS,
    inStock: true,
    isBestSeller: true,
  },

  // BUNDLE PRODUCTS
  {
    id: 'malrich-buy2-get1',
    title: 'MalRich Buy 2 Get 1 Free Bundle',
    slug: 'malrich-buy-2-get-1-free-bundle',
    brand: 'MalRich',
    flavor: 'Vanilla',
    weight: '3 x 400g Tins',
    price: 3900,
    compareAtPrice: 5000,
    badge: 'Super Saver',
    rating: 5.0,
    reviewCount: 67,
    category: 'Bundles',
    shortDescription: 'Get 3 full-sized MalRich 400g nutrition tins for the price of two. Choose your favorite flavor mix upon order.',
    description: 'Our most popular value pack! Receive 3 tins of MalRich Healthy Weight Gain / Balanced Diet supplement (Vanilla, Chocolate, Strawberry, or mixed) and save over Rs. 1,100 with free nationwide shipping included.',
    keyNutrients: [
      'Total 1200g of high potency nutrition powder',
      'Full monthly supply for rapid results',
      'Magnesium, Calcium, Zinc & Multivitamins'
    ],
    benefits: [
      'Unbeatable cost savings (Over 22% OFF)',
      'Free Express Delivery across Pakistan',
      'Guaranteed authentic factory sealed packaging'
    ],
    usageInstructions: 'Take 2 servings daily across 45 days for optimal healthy weight gain transformation.',
    storageInfo: 'Keep sealed tins in cool dry room temperature.',
    images: [bundleImg, vanillaImg, chocolateImg, strawberryImg],
    inStock: true,
    isBestSeller: true,
    isBundle: true,
  },
  {
    id: 'malrich-family-bundle',
    title: 'MalRich Family Bundle',
    slug: 'malrich-family-bundle',
    brand: 'MalRich',
    flavor: 'Chocolate',
    weight: '4 Tins Mega Set',
    price: 4000,
    compareAtPrice: 5000,
    badge: 'Family Value',
    rating: 5.0,
    reviewCount: 58,
    category: 'Bundles',
    shortDescription: 'The ultimate household health pack providing balanced daily nutrients for parents, children, and grandparents.',
    description: 'The MalRich Family Bundle is crafted to supply complete daily dietary nutrition for your entire family. Includes a combination of Vanilla, Chocolate, Mango, and Strawberry tins.',
    keyNutrients: [
      'Comprehensive family nutrient blend',
      'High protein, minerals, and vitamins'
    ],
    benefits: [
      'Caters to different flavor preferences in one family',
      'Maximum savings with free Cash on Delivery shipping',
      'Provides 60+ servings of healthy milk shakes'
    ],
    usageInstructions: 'Serve 1 glass daily for every family member.',
    storageInfo: 'Store in dry place.',
    images: [bundleImg, mangoImg, vanillaImg],
    inStock: true,
    isBestSeller: true,
    isBundle: true,
  }
];

export function getProductById(id: string): Product | undefined {
  return PRODUCTS.find(p => p.id === id || p.slug === id);
}

export function getProductByFlavor(flavor: string): Product | undefined {
  return PRODUCTS.find(p => p.flavor.toLowerCase() === flavor.toLowerCase() && !p.isBundle);
}
